"use client";
import { useState } from "react";
import { LayoutDashboard, LogOut, UserRound } from "lucide-react";
import { toast } from "sonner";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "@/shared/components/ui/avatar";
import { Spinner } from "@/shared/components/ui/spinner";
import { authClient } from "@/shared/config/authClient.config";
import { getInitials } from "@/shared/lib/utils";
import { useNavigate } from "@/shared/hooks/use-navigate";

type Props = {
    image?: string | null;
    name: string;
    email: string;
};

const DropDownMenu = ({ image, name, email }: Props) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const handleLogout = async () => {
        await authClient.signOut({
            fetchOptions: {
                onRequest: () => setIsLoading(true),
                onError() {
                    setIsLoading(false);
                    toast.error("Failed to sign out");
                },
                onSuccess() {
                    setIsLoading(false);
                    toast.success("Signed out");
                    navigate.replace("/login");
                },
            },
        });
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger className="rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 focus:ring-offset-background">
                <Avatar className="size-9 ring-2 ring-transparent transition-all hover:ring-primary/30">
                    <AvatarImage src={image ?? undefined} alt={name} />
                    <AvatarFallback className="bg-primary/15 text-sm font-bold text-primary">
                        {getInitials(name)}
                    </AvatarFallback>
                </Avatar>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="end"
                sideOffset={10}
                className="w-64 p-1"
            >
                <div className="flex items-center gap-3 px-3 py-2">
                    <Avatar className="size-10">
                        <AvatarImage src={image ?? undefined} alt={name} />
                        <AvatarFallback className="bg-primary/15 text-sm font-bold text-primary">
                            {getInitials(name)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-foreground">
                            {name}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                            {email}
                        </p>
                    </div>
                </div>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                    onClick={() => navigate("/dashboard")}
                    className="flex cursor-pointer items-center gap-2"
                >
                    <LayoutDashboard className="size-4" />
                    Dashboard
                </DropdownMenuItem>

                <DropdownMenuItem
                    onClick={() => navigate("/account")}
                    className="flex cursor-pointer items-center gap-2"
                >
                    <UserRound className="size-4" />
                    Account settings
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                    closeOnClick={!isLoading}
                    variant="destructive"
                    onClick={handleLogout}
                    className="flex cursor-pointer items-center justify-between gap-2"
                >
                    <span className="flex items-center gap-2">
                        {isLoading ? (
                            <Spinner className="size-4" />
                        ) : (
                            <LogOut className="size-4" aria-hidden />
                        )}
                        Sign Out
                    </span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default DropDownMenu;
